import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { GridApi } from 'ag-grid-community';
import { groupBy } from 'lodash';
import { DateTime } from 'luxon';
import { REDUX_SLICE_KEYS } from 'common/constants';
import { RootState } from 'store';
import { formattedDate } from 'utils/dateUtils';
import {
  AuthorizationPaymentListItem,
  AuthorizePaymentPageState,
  AuthorizePaymentReviewType,
  UpdateInvoiceDescriptionPayload
} from './types';
import type {
  UpdateInvoiceDescriptionAction,
  VendorsTableDataType
} from 'pages/VendorsTablePage/components/VendorsTable/types';

export const initialState: AuthorizePaymentPageState = {
  authorizePaymentReview: {
    paymentDate: formattedDate(DateTime.now().toISODate()),
    cashInBankAccount: '',
    postingDescription: '',
    companyId: '',
    authorizationPaymentList: []
  },
  selectedInvoices: [],
  gridApi: null,
  updatedInvoiceDescriptions: [],
  shouldUpdateInvoiceDescription: false
};

export const storeSlice = createSlice({
  name: REDUX_SLICE_KEYS.authorizeReview,
  initialState,
  reducers: {
    updateVendorInvoiceRecords: (
      state,
      action: PayloadAction<AuthorizationPaymentListItem[]>
    ) => {
      state.authorizePaymentReview.authorizationPaymentList = action.payload;
    },
    setAuthorizePaymentData: (
      state,
      action: PayloadAction<AuthorizePaymentReviewType>
    ) => {
      state.authorizePaymentReview = action.payload;
    },
    setAgGridParamsApi: (
      state,
      action: PayloadAction<GridApi<VendorsTableDataType>>
    ) => {
      state.gridApi = action.payload;
    },
    addUpdatedInvoiceDescription: (
      state,
      action: PayloadAction<UpdateInvoiceDescriptionAction>
    ) => {
      const updateIndex = state.updatedInvoiceDescriptions.findIndex(
        ({ vendorNo, invoiceId }) =>
          vendorNo === action.payload.vendorNo &&
          invoiceId === action.payload.invoiceId
      );
      if (updateIndex >= 0) {
        state.updatedInvoiceDescriptions[updateIndex].description =
          action.payload.description;
      } else {
        state.updatedInvoiceDescriptions.push(action.payload);
      }
    },
    removeUpdatedInvoiceDescription: (
      state,
      action: PayloadAction<Omit<UpdateInvoiceDescriptionAction, 'description'>>
    ) => {
      const updateIndex = state.updatedInvoiceDescriptions.findIndex(
        ({ vendorNo, invoiceId }) =>
          vendorNo === action.payload.vendorNo &&
          invoiceId === action.payload.invoiceId
      );
      if (updateIndex >= 0) {
        state.updatedInvoiceDescriptions.splice(updateIndex, 1);
      }
    },
    clearUpdatedInvoiceDescriptions: state => {
      state.updatedInvoiceDescriptions = [];
    }
  }
});

const selectSelf = (state: RootState) => state;

export const getUpdatedInvoiceDescriptions = createSelector(
  selectSelf,
  ({ authorizeReview }) => authorizeReview.updatedInvoiceDescriptions
);

export const getUpdatedInvoiceDescriptionsGrouped = createSelector(
  getUpdatedInvoiceDescriptions,
  updatedInvoiceDescriptions => groupBy(updatedInvoiceDescriptions, 'vendorNo')
);

export const getUpdateInvoiceDescriptionsPayload = createSelector(
  getUpdatedInvoiceDescriptionsGrouped,
  (updatedInvoiceDescriptionsGrouped): UpdateInvoiceDescriptionPayload =>
    Object.entries(updatedInvoiceDescriptionsGrouped).map(
      ([vendorId, invoices]) => ({
        vendorId,
        invoices: invoices.map(invoice => ({
          invoiceId: invoice.invoiceId,
          invoiceDescription: invoice.description
        }))
      })
    )
);

export const {
  updateVendorInvoiceRecords,
  setAuthorizePaymentData,
  setAgGridParamsApi,
  addUpdatedInvoiceDescription,
  removeUpdatedInvoiceDescription,
  clearUpdatedInvoiceDescriptions
} = storeSlice.actions;

export default storeSlice.reducer;
